import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { apiService } from '../../../../services/api';
import ImageWithBasePath from '../../../../core/common/imageWithBasePath';
import { Anotacao, Arquivo, AnotacaoAPI } from '../../../../types/notes';
import { NotesModal } from './NotesModal';
import EditNoteModal from './EditNoteModal';

interface AnotacoesSectionProps {
    oportunidadeId: number;
    readOnly?: boolean;
}

const AnotacoesSection: React.FC<AnotacoesSectionProps> = ({ oportunidadeId, readOnly = false }) => {
    const [anotacoes, setAnotacoes] = useState<Anotacao[]>([]);
    const [loadingAnotacoes, setLoadingAnotacoes] = useState(false);
    const [anotacaoPage, setAnotacaoPage] = useState(1);
    const [anotacaoTotalPages, setAnotacaoTotalPages] = useState(1);
    const [anotacaoTotal, setAnotacaoTotal] = useState(0);
    const [loadingMoreAnotacoes, setLoadingMoreAnotacoes] = useState(false);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [showEditNoteModal, setShowEditNoteModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState<Anotacao | null>(null);
    const [showEditNoteSuccessModal, setShowEditNoteSuccessModal] = useState(false);
    const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
    const [showDeleteNoteSuccessModal, setShowDeleteNoteSuccessModal] = useState(false);
    const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
    const [isDeletingNote, setIsDeletingNote] = useState(false);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (tipo: string): string => {
        if (tipo.includes('image')) return 'photo';
        if (tipo.includes('pdf')) return 'file-text';
        if (tipo.includes('spreadsheet') || tipo.includes('excel')) return 'table';
        if (tipo.includes('word') || tipo.includes('document')) return 'file-text';
        if (tipo.includes('zip') || tipo.includes('rar')) return 'archive';
        return 'file';
    };

    const fetchAnotacoes = async (page = 1, append = false) => {
        if (!oportunidadeId) return;
        try {
            append ? setLoadingMoreAnotacoes(true) : setLoadingAnotacoes(true);
            const response = await apiService.getAnotacoes({
                id_oportunidade: oportunidadeId,
                page,
                per_page: 10
            });
            if (response.success && Array.isArray(response.data)) {
                const anotacoesData = response.data as unknown as AnotacaoAPI[];
                const anotacoesProcessadas: Anotacao[] = anotacoesData.map(note => ({
                    ...note,
                    arquivos: note.arquivos ? JSON.parse(note.arquivos) as Arquivo[] : []
                }));
                setAnotacoes(prev => append ? [...prev, ...anotacoesProcessadas] : anotacoesProcessadas);
                if (response.pagination) {
                    setAnotacaoPage(response.pagination.page);
                    setAnotacaoTotalPages(response.pagination.total_pages);
                    setAnotacaoTotal(response.pagination.total);
                }
            }
        } catch (error) {
            console.error('Erro ao carregar anotações:', error);
        } finally {
            append ? setLoadingMoreAnotacoes(false) : setLoadingAnotacoes(false);
        }
    };

    useEffect(() => {
        fetchAnotacoes();
    }, [oportunidadeId]);

    const handlePrepareEditNote = (note: Anotacao) => {
        setSelectedNote(note);
        setShowEditNoteModal(true);
    };

    const handleUpdateNote = async (id: number, formData: FormData) => {
        try {
            await apiService.updateAnotacao(id, {
                titulo: formData.get('titulo') as string,
                observacoes: formData.get('observacoes') as string,
                arquivos: Array.from(formData.getAll('arquivos[]')) as File[],
                remover_arquivos: formData.get('remover_arquivos')
                    ? JSON.parse(formData.get('remover_arquivos') as string)
                    : undefined
            });
            setShowEditNoteModal(false);
            setShowEditNoteSuccessModal(true);
            setTimeout(() => {
                fetchAnotacoes();
                setShowEditNoteSuccessModal(false);
            }, 2000);
        } catch (error) {
            console.error('Erro ao atualizar anotação:', error);
        }
    };

    const handlePrepareDeleteNote = (e: React.MouseEvent, noteId: number) => {
        e.preventDefault();
        setSelectedNoteId(noteId);
        setShowDeleteNoteModal(true);
    };

    const handleDeleteNote = async () => {
        if (!selectedNoteId) return;
        try {
            setIsDeletingNote(true);
            await apiService.deleteAnotacao(selectedNoteId);
            setShowDeleteNoteModal(false);
            setShowDeleteNoteSuccessModal(true);
            setTimeout(() => {
                fetchAnotacoes();
                setShowDeleteNoteSuccessModal(false);
            }, 2000);
        } catch (error) {
            console.error('Erro ao excluir anotação:', error);
        } finally {
            setIsDeletingNote(false);
        }
    };

    return (
        <>
            <div className="card">
                <div className="card-header d-flex justify-content-between">
                    <h4 className="fw-semibold">Anotações</h4>
                    {!readOnly && (
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setShowNotesModal(true)}
                        >
                            <i className="ti ti-plus"></i> Nova Anotação
                        </button>
                    )}
                </div>
                <div className="card-body">
                    {loadingAnotacoes ? (
                        <div className="text-center p-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : anotacoes.length > 0 ? (
                        <div className="notes-activity">
                            {anotacoes.map((note) => (
                                <div className="card mb-3" key={note.id}>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center justify-content-between pb-2">
                                            <div className="d-inline-flex align-items-center mb-2">
                                                <span className="avatar avatar-sm avatar-rounded avatar-gray me-2 flex-shrink-0">
                                                    <ImageWithBasePath
                                                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(note.usuario_nome)}&background=0D8ABC&color=fff`}
                                                        alt={note.usuario_nome}
                                                    />
                                                </span>
                                                <div>
                                                    <h6 className="fw-medium mb-1">{note.usuario_nome}</h6>
                                                    <p className="mb-0">
                                                        {new Date(note.data_criacao).toLocaleDateString('pt-BR', {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </p>
                                                </div>
                                            </div>
                                            {!readOnly && (
                                                <div className="mb-2">
                                                    <div className="d-flex gap-2 contacts-action">
                                                        <Link
                                                            to="#"
                                                            onClick={(e) => { e.preventDefault(); handlePrepareEditNote(note); }}
                                                        >
                                                            <i className="ti ti-edit text-blue"></i> Editar
                                                        </Link>
                                                        <Link
                                                            to="#"
                                                            onClick={(e) => handlePrepareDeleteNote(e, note.id)}
                                                        >
                                                            <i className="ti ti-trash text-danger"></i> Excluir
                                                        </Link>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <h5 className="fw-medium mb-1">{note.titulo}</h5>
                                        <p style={{ whiteSpace: 'pre-line' }}>{note.observacoes}</p>

                                        {note.arquivos && note.arquivos.length > 0 && (
                                            <div className="d-inline-flex align-items-center flex-wrap">
                                                {note.arquivos.map((arquivo: Arquivo, index: number) => (
                                                    <div className="note-download me-3" key={index}>
                                                        <div className="note-info">
                                                            <span className="note-icon bg-secondary-success">
                                                                <i className={`ti ti-${getFileIcon(arquivo.tipo)}`} />
                                                            </span>
                                                            <div>
                                                                <h6 className="fw-medium mb-1">{arquivo.nome_original}</h6>
                                                                <p>{formatFileSize(arquivo.tamanho)}</p>
                                                            </div>
                                                        </div>
                                                        <Link
                                                            to="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                apiService.downloadArquivoAnotacao(note.id, arquivo.nome_salvo);
                                                            }}
                                                        >
                                                            <i className="ti ti-arrow-down" />
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {anotacaoPage < anotacaoTotalPages && (
                                <div className="text-center mt-3">
                                    <button
                                        className="btn btn-outline-primary"
                                        style={{ fontSize: '1rem', padding: '0.6rem 2rem' }}
                                        onClick={() => fetchAnotacoes(anotacaoPage + 1, true)}
                                        disabled={loadingMoreAnotacoes}
                                    >
                                        {loadingMoreAnotacoes ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                Carregando...
                                            </>
                                        ) : (
                                            `Carregar mais (${anotacoes.length} de ${anotacaoTotal})`
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p>Sem anotações registradas</p>
                    )}
                </div>
            </div>

            {!readOnly && (
                <>
                    <NotesModal
                        showNotesModal={showNotesModal}
                        setShowNotesModal={setShowNotesModal}
                        opportunityId={oportunidadeId}
                        onNoteCreated={() => fetchAnotacoes()}
                    />

                    <EditNoteModal
                        show={showEditNoteModal}
                        onHide={() => setShowEditNoteModal(false)}
                        note={selectedNote}
                        onSave={handleUpdateNote}
                    />

                    <Modal show={showDeleteNoteModal} onHide={() => setShowDeleteNoteModal(false)} centered>
                        <Modal.Body>
                            <div className="text-center">
                                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                                    <i className="ti ti-trash-x fs-36 text-danger" />
                                </div>
                                <h4 className="mb-2">Remover Anotação?</h4>
                                <p className="mb-0">
                                    Tem certeza que deseja remover esta anotação? Esta ação não poderá ser desfeita.
                                </p>
                                <div className="d-flex align-items-center justify-content-center mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-light me-2"
                                        onClick={() => setShowDeleteNoteModal(false)}
                                        disabled={isDeletingNote}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleDeleteNote}
                                        disabled={isDeletingNote}
                                    >
                                        {isDeletingNote ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                Excluindo...
                                            </>
                                        ) : 'Sim, Excluir'}
                                    </button>
                                </div>
                            </div>
                        </Modal.Body>
                    </Modal>

                    <Modal show={showDeleteNoteSuccessModal} onHide={() => setShowDeleteNoteSuccessModal(false)} centered>
                        <Modal.Body>
                            <div className="success-message text-center">
                                <div className="success-popup-icon bg-success-light">
                                    <i className="ti ti-check-check fs-36 text-success" />
                                </div>
                                <h3>Anotação Excluída com Sucesso!</h3>
                                <p>A anotação foi removida permanentemente do sistema.</p>
                            </div>
                        </Modal.Body>
                    </Modal>

                    <Modal show={showEditNoteSuccessModal} onHide={() => setShowEditNoteSuccessModal(false)} centered>
                        <Modal.Body>
                            <div className="success-message text-center">
                                <div className="success-popup-icon bg-success-light">
                                    <i className="ti ti-check-check fs-36 text-success" />
                                </div>
                                <h3>Anotação Atualizada com Sucesso!</h3>
                                <p>As alterações foram salvas com sucesso.</p>
                            </div>
                        </Modal.Body>
                    </Modal>
                </>
            )}
        </>
    );
};

export default AnotacoesSection;
